// PaymentModal.js
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faCreditCard,
  faMoneyBill,
  faMobile,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });

const PaymentModal = ({ onClose, orderUuid, balance, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || parseFloat(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    if (parseFloat(amount) > balance) {
      setError(`Amount cannot exceed balance of GH₵${balance.toFixed(2)}`);
      return;
    }

    setProcessing(true);
    setError("");

    try {
      // Get CSRF token
      const csrfResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/session/token`,
        {
          credentials: "include",
          headers: { Accept: "text/plain" },
        }
      );
      const csrfToken = await csrfResponse.text();

      // Get current user
      const user = ls.get("currentUser");

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/pos/payments/create`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            order_uuid: orderUuid,
            amount: parseFloat(amount),
            payment_method: paymentMethod,
            reference: `PAY-${Date.now().toString().slice(-6)}`,
            notes: "Payment added via invoice",
            cashier_uid: user?.uid || 1,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        onSuccess(result);
        setAmount("");
        onClose();
      } else {
        throw new Error(result.message || "Payment failed");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={modalStyles.content}>
      <div style={modalStyles.balanceInfo}>
        <p>
          Balance Due: <strong>GH₵{balance.toFixed(2)}</strong>
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={modalStyles.formGroup}>
          <label style={modalStyles.amountlable}>Amount (GH₵)</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            max={balance}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={modalStyles.input}
            placeholder="Enter amount"
            required
          />
        </div>

        <div style={modalStyles.formGroup}>
          <label style={modalStyles.amountlable}>Payment Method</label>
          <div style={modalStyles.methodButtons}>
            <button
              type="button"
              style={{
                ...modalStyles.methodButton,
                ...(paymentMethod === "cash" && modalStyles.methodButtonActive),
              }}
              onClick={() => setPaymentMethod("cash")}
            >
              <FontAwesomeIcon icon={faMoneyBill} /> Cash
            </button>
            <button
              type="button"
              style={{
                ...modalStyles.methodButton,
                ...(paymentMethod === "mobile_money" &&
                  modalStyles.methodButtonActive),
              }}
              onClick={() => setPaymentMethod("mobile_money")}
            >
              <FontAwesomeIcon icon={faMobile} /> Mobile Money
            </button>
            <button
              type="button"
              style={{
                ...modalStyles.methodButton,
                ...(paymentMethod === "credit_card" &&
                  modalStyles.methodButtonActive),
              }}
              onClick={() => setPaymentMethod("credit_card")}
            >
              <FontAwesomeIcon icon={faCreditCard} /> Card
            </button>
          </div>
        </div>

        {error && <div style={modalStyles.error}>{error}</div>}

        <div style={modalStyles.footer}>
          <button
            type="button"
            style={modalStyles.cancelButton}
            onClick={onClose}
            disabled={processing}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={modalStyles.submitButton}
            disabled={processing}
          >
            {processing ? (
              "Processing..."
            ) : (
              <>
                <FontAwesomeIcon icon={faCheckCircle} /> Add Payment
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "white",
    borderRadius: "12px",
    width: "400px",
    maxWidth: "90%",
    maxHeight: "90vh",
    overflow: "auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    borderBottom: "1px solid #e0e0e0",
  },
  title: {
    margin: 0,
    fontSize: "20px",
    color: "#333",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    color: "#666",
    padding: 0,
  },
  content: {
    padding: "20px",
  },
  balanceInfo: {
    fontSize: "17px",
    padding: "15px",
    borderRadius: "8px",
    marginBottom: "20px",
    textAlign: "center",
  },
  formGroup: {
    marginBottom: "20px",
  },
  input: {
    width: "100%",
    padding: "14px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "16px",
    marginTop: "8px",
  },
  methodButtons: {
    display: "flex",
    gap: "10px",
    marginTop: "8px",
  },
  methodButton: {
    flex: 1,
    padding: "10px",
    background: "#f8f9fa",
    border: "2px solid #e0e0e0",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "14px",
  },
  methodButtonActive: {
    background: "#e3f2fd",
    borderColor: "#2196f3",
    color: "#1976d2",
  },
  error: {
    background: "#f8d7da",
    color: "#721c24",
    padding: "10px",
    borderRadius: "6px",
    marginBottom: "20px",
    fontSize: "14px",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    paddingTop: "20px",
    borderTop: "1px solid #e0e0e0",
  },
  cancelButton: {
    padding: "10px 20px",
    background: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  submitButton: {
    padding: "10px 20px",
    background: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  amountlable: {
    fontSize: "14px",
    fontWeight: "bold",
  },
};

export default PaymentModal;
